import { createListCollection, Field as ChakraField, HStack, Icon, Text } from '@chakra-ui/react';
import ModifiedTag from '@components/common/form/ModifiedField';
import MultiSelect from '@components/ui/multiSelect/MultiSelect';
import RequiredIndicator from '@components/ui/RequiredIndicator';
import { CustomSelect } from '@components/ui/select/Select';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectActiveGroupsPlatforms } from '@redux/config/config.selectors';
import { getPlatformsCodesFromMarks, haveSameUniqueElements } from '@utils/common.utils';
import { getClassificationIcon } from '@utils/target.utils';
import { hasChanged, isInvalid } from '@utils/validation/validation.utils';
import { useFormikContext } from 'formik';
import { FormattedMessage, useIntl } from 'react-intl';

import { MarkEnum } from '@/types/commons/commons.types';
import { ClassificationEnum } from '@/types/sensor/classification.types';
import { EditableType, GetSensorOrSubSensorConfigurationWithShorthand } from '@/types/sensor/sensorInstallation.types';

export type SensorFormType = Partial<GetSensorOrSubSensorConfigurationWithShorthand<EditableType>>;

export function PlatformInput() {
  const { initialValues, touched, errors, values, setFieldValue, setFieldTouched } =
    useFormikContext<Partial<GetSensorOrSubSensorConfigurationWithShorthand<EditableType>>>();
  const platforms = useAppSelector(selectActiveGroupsPlatforms);
  const currentPlatforms = getPlatformsCodesFromMarks(values.marks ?? []);

  if (platforms.length <= 1) {
    if (currentPlatforms.length === 0) {
      setFieldValue(
        'marks',
        platforms.map((value) => ({ context: value.code, type: MarkEnum.PLATFORM })),
      );
    }
    return null;
  }

  return (
    <ChakraField.Root variant="cockpit" required invalid={isInvalid('marks', touched, errors)}>
      <ChakraField.Label width="100%" height="20px">
        <FormattedMessage id="components.sensorInstallation.form.common.platforms" />
        <RequiredIndicator />
        {!haveSameUniqueElements(getPlatformsCodesFromMarks(initialValues.marks ?? []), currentPlatforms) && (
          <ModifiedTag />
        )}
      </ChakraField.Label>
      <MultiSelect
        values={currentPlatforms}
        label="components.sensorInstallation.form.common.addPlatforms"
        collection={createListCollection({
          items: platforms.map((platform) => ({
            label: platform.name,
            value: platform.code,
          })),
        })}
        onChange={async (values) => {
          await setFieldTouched('marks', true);
          setFieldValue(
            'marks',
            values.map((value) => ({ context: value, type: MarkEnum.PLATFORM })),
          );
        }}
      />
    </ChakraField.Root>
  );
}

export function ClassificationInput() {
  const { formatMessage } = useIntl();
  const { values, initialValues, setFieldValue } = useFormikContext<SensorFormType>();
  return (
    <ChakraField.Root variant="cockpit">
      <ChakraField.Label width="100%" height="20px">
        <FormattedMessage id="components.sensorInstallation.form.common.defaultTargetClassification" />
        <RequiredIndicator />
        {hasChanged('defaultTargetClassification', initialValues, values) && <ModifiedTag />}
      </ChakraField.Label>
      <CustomSelect
        value={values.defaultTargetClassification}
        collection={createListCollection({
          items: Object.keys(ClassificationEnum).map((classification) => ({
            value: classification,
            label: formatMessage({ id: `target.classification.${classification}` }),
          })),
        })}
        formatLabel={(option) => (
          <HStack gap={1.5} width="100%">
            <Icon boxSize="24px" as={getClassificationIcon(option.value as ClassificationEnum)} />
            <Text textStyle="screenStd" color="neutral.white">
              {option.label}
            </Text>
          </HStack>
        )}
        onChange={(value) => setFieldValue('defaultTargetClassification', value)}
      />
    </ChakraField.Root>
  );
}
